/**
 * Un classement écrit à la main sur la feuille de brouillon : « 1. (avatar) Léa … 245 HORA »,
 * ma ligne à l'encre du prof, « (toi) » — pas entourée. Fin d'un live joué sur la feuille.
 */

'use client';

import './paper.css';
import { cn } from '../../lib/utils';
import { AvatarCircle } from '../avatar-circle';

export interface HandRankingRow {
  rank: number;
  name: string;
  score: number;
  isMe?: boolean;
  /** photo du joueur (sinon ses initiales) */
  avatarUrl?: string | null;
}

/** Les lignes à écrire : les `top` premiers, puis ma ligne si je suis plus loin. */
export function handRankingLines(rows: readonly HandRankingRow[], top = 10): HandRankingRow[] {
  const sorted = [...rows].sort((a, b) => a.rank - b.rank);
  const head = sorted.slice(0, top);
  const me = sorted.find((r) => r.isMe);
  return me && !head.includes(me) ? [...head, me] : head;
}

export function HandRanking({
  rows,
  unit,
  title = 'Classement',
  top = 10,
  className,
}: {
  rows: readonly HandRankingRow[];
  unit: string;
  title?: string;
  top?: number;
  className?: string;
}) {
  const lines = handRankingLines(rows, top);
  if (lines.length === 0) return null;
  return (
    <div className={cn('pp-hand-ranking', className)} data-testid="hand-ranking">
      <p className="pp-hand-ranking-title">{title}</p>
      <ol>
        {lines.map((r, i) => {
          const gap = i > 0 && r.rank > lines[i - 1]!.rank + 1;
          return (
            <li
              key={`${r.rank}-${r.name}`}
              className={cn('pp-hand-ranking-line', r.isMe && 'pp-hand-ranking-me', gap && 'mt-2')}
              aria-current={r.isMe ? 'true' : undefined}
            >
              <span className="pp-hand-ranking-rank">{r.rank}.</span>
              <AvatarCircle
                name={r.name}
                src={r.avatarUrl ?? null}
                size="sm"
                className="pp-hand-ranking-avatar"
              />
              <span className="pp-hand-ranking-name">{r.isMe ? `${r.name} (toi)` : r.name}</span>
              <span className="pp-hand-ranking-score">
                {r.score} {unit}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
